<script lang="ts">
    import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
    import type { RewardsTranslationNamespaces } from "@apps/rewards/constants/constants";
    import {
        REWARDS_BRANDING_ICONS,
        REWARDS_HOME_ANCHOR,
    } from "@apps/rewards/constants/constants";
    import { REWARDS_BRANDING_LAUNCHER_STYLE_TYPES } from "@apps/rewards/constants/rewardsBrandingTypes";
    import { RewardsPopUpRootWidgetDefinition } from "@apps/rewards/definitions";
    import { REWARDS_SDK_METHODS } from "@apps/rewards/rewards_sdk/constants";
    import type CloseRewardsPopupSdkMethodPayload from "@apps/rewards/rewards_sdk/methods/CloseRewardsPopupSdkMethodPayload";
    import type OpenRewardsPopupSdkMethodPayload from "@apps/rewards/rewards_sdk/methods/OpenRewardsPopupSdkMethodPayload";
    import type RewardsStorefrontValuesService from "@apps/rewards/services/RewardsStorefrontValuesService";
    import type LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
    import type { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import type { ExtractModuleType } from "@modules/dynamic_import/types";
    import type { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import WidgetClosedSdkEvent from "@modules/sdk/common_sdk/events/WidgetClosedSdkEvent";
    import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
    import WidgetOpenedSdkEvent from "@modules/sdk/common_sdk/events/WidgetOpenedSdkEvent";
    import {
        dispatchSdkEvent,
        subscribeToSdkMethod,
    } from "@modules/sdk/helpers";
    import { focusTrap } from "@ui-utils/actions/focusTrap";
    import { skip } from "rxjs";
    import { onMount } from "svelte";

    import Widget from "@components/Widget.svelte";
    import type { LoggerInterface } from "@interfaces/LoggerInterface";

    import RewardsPopUpWidgetContainer from "./components/RewardsPopUpWidgetContainer.svelte";
    import RewardsPopUpWidgetLauncherButton from "./components/RewardsPopUpWidgetLauncher/RewardsPopUpWidgetLauncherButton.svelte";
    import RewardsPopUpWidgetTheme from "./components/RewardsPopUpWidgetTheme.svelte";
    import type RewardsPopUpWidgetLauncherManager from "./utils/RewardsPopUpWidgetLauncherManager/RewardsPopUpWidgetLauncherManager";

    //Props
    export let gwI18n: GwI18n;
    export let rewardsStorefrontValuesService: RewardsStorefrontValuesService;
    export let dynamicImportManager: DynamicImportManager;
    export let launcherManager: RewardsPopUpWidgetLauncherManager;
    export let locationController: LocationControllerInterface;
    export let logger: LoggerInterface;
    export let widgetName: REWARDS_WIDGET_NAMES;
    export let widgetNamespace: RewardsTranslationNamespaces;
    export let afterWidgetInject: () => void;

    //Variables
    const rewardsBranding$ =
        rewardsStorefrontValuesService.selectRewardsBranding();
    const isPopUpWidgetOpen$ = launcherManager.isPopUpWidgetOpen$;

    const openRewardsPopupSubscription =
        subscribeToSdkMethod<OpenRewardsPopupSdkMethodPayload>(
            REWARDS_SDK_METHODS.OPEN_REWARDS_POPUP,
            ({ onSuccess }) => {
                launcherManager.rewardsPopUpWidgetShow();
                onSuccess();
            }
        );

    const closeRewardsPopupSubscription =
        subscribeToSdkMethod<CloseRewardsPopupSdkMethodPayload>(
            REWARDS_SDK_METHODS.CLOSE_REWARDS_POPUP,
            ({ onSuccess }) => {
                launcherManager.rewardsPopUpWidgetHide();
                onSuccess();
            }
        );

    const openPopUpWidgetSubscription = isPopUpWidgetOpen$
        .pipe(skip(1))
        .subscribe((isOpen) => {
            if (isOpen) {
                dispatchSdkEvent(new WidgetOpenedSdkEvent(widgetName));
            } else {
                dispatchSdkEvent(new WidgetClosedSdkEvent(widgetName));
            }
        });

    let RewardsPopUpRootWidget: ExtractModuleType<
        typeof RewardsPopUpRootWidgetDefinition
    >["default"];

    const importRewardsPopUpRootWidget = async () => {
        if (RewardsPopUpRootWidget) return;

        try {
            RewardsPopUpRootWidget = await dynamicImportManager.importDefault(
                RewardsPopUpRootWidgetDefinition,
                true
            );
        } catch (error) {
            console.error(
                "Failed to load RewardsPopUpRootWidget module",
                error
            );
        }
    };

    const handleRewardsPopUpRootWidgetInit = () => {
        dispatchSdkEvent(
            new WidgetInjectedSdkEvent(
                REWARDS_WIDGET_NAMES.REWARDS_POP_UP_ROOT_WIDGET
            )
        );
    };

    onMount(() => {
        afterWidgetInject();

        return () => {
            openRewardsPopupSubscription.unsubscribe();
            closeRewardsPopupSubscription.unsubscribe();
            openPopUpWidgetSubscription.unsubscribe();
        };
    });

    $: if ($isPopUpWidgetOpen$) importRewardsPopUpRootWidget();
</script>

{#if $rewardsBranding$}
    <RewardsPopUpWidgetTheme rewardsBranding={$rewardsBranding$}>
        {@const widgetPosition =
            $rewardsBranding$.popUpWidget.launcher.position.placement}
        {@const launcherStyle =
            $rewardsBranding$.popUpWidget.launcher.appearance.style}

        <div
            use:focusTrap={{
                enabled: $isPopUpWidgetOpen$,
                options: {
                    allowOutsideClick: true,
                    clickOutsideDeactivates: true,
                },
            }}
        >
            <RewardsPopUpWidgetLauncherButton
                {launcherManager}
                isPopUpWidgetOpen={$isPopUpWidgetOpen$}
                openPopUpWidgetOnLoad={locationController
                    .getHashParams()
                    .has(REWARDS_HOME_ANCHOR)}
                {gwI18n}
                icon={REWARDS_BRANDING_ICONS[
                    $rewardsBranding$.popUpWidget.launcher.icon.type
                ]}
                hideIcon={launcherStyle ===
                    REWARDS_BRANDING_LAUNCHER_STYLE_TYPES.textOnly}
                hideText={launcherStyle ===
                    REWARDS_BRANDING_LAUNCHER_STYLE_TYPES.iconOnly}
                {widgetPosition}
                {widgetNamespace}
                on:click={() => launcherManager.rewardsPopUpWidgetToggle()}
                on:mouseover={importRewardsPopUpRootWidget}
            />

            {#if $isPopUpWidgetOpen$ && RewardsPopUpRootWidget}
                <RewardsPopUpWidgetContainer
                    {widgetPosition}
                    onClose={() => launcherManager.rewardsPopUpWidgetHide()}
                >
                    <Widget
                        class="gw-rd-popup-root-widget-container"
                        widget={RewardsPopUpRootWidget}
                        params={[
                            {
                                isWidgetShadowRootEnabled: true,
                            },
                            logger,
                        ]}
                        on:widgetInit={handleRewardsPopUpRootWidgetInit}
                    />
                </RewardsPopUpWidgetContainer>
            {/if}
        </div>
    </RewardsPopUpWidgetTheme>
{/if}

<style lang="postcss">
    :global(.gw-rd-popup-root-widget-container) {
        height: 100%;
    }
</style>
